export type HostEventTransportResult = 'sent' | 'queued' | 'rejected';

/**
 * A beam dispatcher the host can inject. `sent` and `queued` are
 * durable attempts. `rejected` leaves no lifecycle dedupe key.
 */
export type HostEventTransport = (
  eventName: string,
  payload: Record<string, unknown>,
) => HostEventTransportResult;

export function readHostEventTransportResult(
  value: unknown,
): HostEventTransportResult {
  if (value === 'sent' || value === 'queued' || value === 'rejected') {
    return value;
  }
  return 'sent';
}

export function shouldRecordLifecycleDedupe(result: unknown): boolean {
  return result !== 'rejected';
}

export interface OrderedBeam {
  readonly args: readonly unknown[];
  readonly url: string;
}

export interface OrderedBeamQueue {
  readonly size: number;
  enqueue(beam: OrderedBeam): 'queued';
  /**
   * Hands beams to `accept` in order. A false return stops the drain
   * and puts that beam and everything after it back at the front.
   */
  drain(accept: (beam: OrderedBeam) => boolean): void;
  clear(): void;
}

/**
 * Bounded FIFO for beams that cannot leave yet. Overflow drops the
 * oldest beam and reports it. The new beam is still queued.
 */
export function createOrderedBeamQueue(
  capacity: number,
  onOverflow?: () => void,
): OrderedBeamQueue {
  const items: OrderedBeam[] = [];
  return {
    get size() {
      return items.length;
    },
    enqueue(beam) {
      if (!(capacity >= 1)) {
        onOverflow?.();
        return 'queued';
      }
      if (items.length >= capacity) {
        items.shift();
        onOverflow?.();
      }
      items.push({ args: beam.args.slice(), url: beam.url });
      return 'queued';
    },
    drain(accept) {
      const pending = items.splice(0, items.length);
      let index = 0;
      try {
        for (; index < pending.length; index += 1) {
          if (!accept(pending[index])) {
            return;
          }
        }
      } finally {
        if (index < pending.length) {
          items.unshift(...pending.slice(index));
        }
      }
    },
    clear() {
      items.length = 0;
    },
  };
}
